import * as swcHelpers from '@swc/helpers';
import { takeLatest, call, select, put } from 'redux-saga/effects';
import { Products } from '@grandvisionhq/api-client';
import { ApiConfigSelectors, assertApiConfig, statusSuccess } from '@grandvisionhq/state';
import { startPrescriptionGlassesConfiguration } from '../actions.js';
import { getBundleFrame, getBundleLensTypes, getBundleLensType, getBundlePackages, getBundlePackage, getBundleAddOns } from './selectors.internal.js';
import { selectLensTypeSuccess, selectPackageSuccess } from '../configurations/actions.js';
import { fetchLensTypes, fetchPackages, fetchAddOns, fetchLensTypesSuccess, fetchLensTypesFailed, fetchPackagesSuccess, fetchPackagesFailed, fetchAddOnsSuccess, fetchAddOnsFailed } from './actions.js';

const defined = (v)=>typeof v !== 'undefined'
;
function* fetchBundleProducts(productType, skus1) {
    if (!skus1.size) {
        return {
            products: [],
            tried: skus1,
            missing: new Set()
        };
    }
    const config = yield select(ApiConfigSelectors.getApiConfig);
    assertApiConfig(config);
    const { results  } = yield call(Products.query, config.productsUrl, {
        expand: [
            'masterVariant.prices[*].discounted.discount',
            'variants[*].prices[*].discounted.discount', 
        ],
        filters: [
            `variants.sku:${encodeURIComponent([
                ...skus1
            ].map((id)=>`"${id}"`
            ).join(','))}`
        ]
    });
    const fetchedSkus = new Set(results.reduce((skus, p)=>[
            ...skus,
            p.masterVariant.sku,
            ...(p.variants || []).map((v)=>v.sku
            )
        ]
    , []).filter(defined));
    const missing = new Set([
        ...skus1
    ].filter((s)=>!fetchedSkus.has(s)
    ));
    return {
        products: results,
        tried: skus1,
        missing
    };
}
/**
 * Lens Types
 */ function* ensureLensTypesAreLoaded({ payload: { frameSku  }  }) {
    yield call(loadMissingLensTypes, frameSku);
}
function* loadMissingLensTypes(frameSku) {
    const frame = yield select(getBundleFrame, {
        frameSku
    });
    if (!frame) {
        return;
    }
    const lensTypes = yield select(getBundleLensTypes);
    const lensTypeSkusToFetch = [
        ...frame.lensTypeSkus
    ].filter((sku)=>!(sku in lensTypes)
    );
    yield put(fetchLensTypes({
        frameSku,
        skus: new Set(lensTypeSkusToFetch)
    }));
}
function* onFetchLensTypes({ payload: { skus , frameSku  }  }) {
    try {
        const result = yield call(fetchBundleProducts, 'lens-type', skus);
        yield put(fetchLensTypesSuccess(swcHelpers.objectSpread({
            frameSku
        }, result)));
    } catch (error) {
        yield put(fetchLensTypesFailed({
            frameSku,
            skus,
            error
        }));
    }
}
/**
 * Packages
 */ function* loadMissingPackages(lensTypeSku) {
    const bundleLensType = yield select(getBundleLensType, {
        lensTypeSku
    });
    if (!bundleLensType || !statusSuccess(bundleLensType)) {
        return;
    }
    const packages = yield select(getBundlePackages);
    const packageSkusToFetch = [
        ...bundleLensType.result.packageSkus
    ].filter((packageSku)=>!(packageSku in packages)
    );
    if (packageSkusToFetch.length) {
        yield put(fetchPackages({
            lensTypeSku,
            skus: new Set(packageSkusToFetch)
        }));
    }
}
function* ensurePackagesAreLoaded({ payload: { lensTypeSku  }  }) {
    yield call(loadMissingPackages, lensTypeSku);
}
function* onFetchPackages({ payload: { skus , lensTypeSku  }  }) {
    try {
        const result = yield call(fetchBundleProducts, 'package', skus);
        yield put(fetchPackagesSuccess(swcHelpers.objectSpread({
            lensTypeSku
        }, result)));
    } catch (error) {
        yield put(fetchPackagesFailed({
            lensTypeSku,
            skus,
            error
        }));
    }
}
/**
 * Add-ons
 */ function* loadMissingAddOns(packageSku) {
    const bundlePackage = yield select(getBundlePackage, {
        packageSku
    });
    if (!bundlePackage || !statusSuccess(bundlePackage)) {
        return;
    }
    const addons = yield select(getBundleAddOns);
    const addOnSkusToFetch = [
        ...bundlePackage.result.addOnSkus
    ].filter((addOnSku)=>!(addOnSku in addons)
    );
    if (addOnSkusToFetch.length) {
        yield put(fetchAddOns({
            packageSku,
            skus: new Set(addOnSkusToFetch)
        }));
    }
}
function* ensureAddOnsAreLoaded({ payload: { packageSku  }  }) {
    yield call(loadMissingAddOns, packageSku);
}
function* onFetchAddOns({ payload: { skus , packageSku  }  }) {
    try {
        const result = yield call(fetchBundleProducts, 'addon', skus);
        yield put(fetchAddOnsSuccess(swcHelpers.objectSpread({
            packageSku
        }, result)));
    } catch (error) {
        yield put(fetchAddOnsFailed({
            packageSku,
            skus,
            error
        }));
    }
}
/**
 * These effects make sure data is loaded as soon as possible
 */ const listenForSelectionChanges = [
    takeLatest(startPrescriptionGlassesConfiguration.type, ensureLensTypesAreLoaded),
    takeLatest(selectLensTypeSuccess.type, ensurePackagesAreLoaded),
    takeLatest(selectPackageSuccess.type, ensureAddOnsAreLoaded), 
];
/**
 * These listen for the actual fetching actions
 */ const listenForFetchEvents = [
    takeLatest(fetchLensTypes.type, onFetchLensTypes),
    takeLatest(fetchPackages.type, onFetchPackages),
    takeLatest(fetchAddOns.type, onFetchAddOns), 
];
var bundleSagas = [
    ...listenForFetchEvents,
    ...listenForSelectionChanges
];

export { bundleSagas as default, fetchBundleProducts, loadMissingAddOns, loadMissingLensTypes, loadMissingPackages, onFetchAddOns, onFetchLensTypes, onFetchPackages };
//# sourceMappingURL=sagas.js.map
